import React from 'react';
import { FiMessageSquare, FiUsers, FiClock, FiAlertTriangle, FiCheckCircle,FiShield, FiBarChart2 } from 'react-icons/fi';

const ERPvsWhatsApp = () => {
  const problems = [
    {
      icon: <FiMessageSquare className="w-6 h-6" />,
      title: 'The Chaos of Multiple Groups',
      stat: 'Teachers manage 10+ WhatsApp groups daily',
      description: 'Walk into any school in India and you\'ll find the same story. There\'s a group for class 10A, another for class 10B, one for the science department, one for the sports committee, a parent-teacher group, and let\'s not forget the admin staff group. Teachers are drowning in notifications, missing important messages, and spending hours scrolling through endless chats to find one piece of information.',
      solution: 'Truyon provides a structured communication system. No more scrolling through 500 messages to find one announcement. Everything is organized by department, class, and topic.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Lost in the Noise',
      stat: '60% of important messages get buried in group chats',
      description: 'A parent message asking about their child\'s attendance gets lost between memes and good morning messages. An important circular from the principal is buried under 200 replies of "thank you ma\'am." Critical information gets missed daily because WhatsApp groups are noisy, unorganized, and impossible to filter.',
      solution: 'Truyon separates important announcements from casual conversation. Official communication is tagged, tracked, and confirmed. No more missed messages.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Working After Hours',
      stat: 'Teachers spend 2+ hours daily responding to WhatsApp messages',
      description: 'The boundary between work and personal life has disappeared. Teachers are expected to respond to parent messages at 9 PM, answer student queries on weekends, and stay "available" 24/7. This constant connectivity is leading to burnout, stress, and teachers leaving the profession.',
      solution: 'Truyon respects work-life balance. Communication happens within defined hours, and everything is documented. Teachers can focus on teaching, not on being available 24/7.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiAlertTriangle className="w-6 h-6" />,
      title: 'No Official Records',
      stat: '90% of school communication has no official record',
      description: 'A parent claims they never received a message about a fee increase. A teacher says they informed parents about a school closure. Who is right? Without official records, schools are constantly at risk of disputes, misunderstandings, and parent complaints. WhatsApp messages disappear, get deleted, or are simply unverifiable.',
      solution: 'Truyon creates an immutable record of all communication. Every announcement, every message, every confirmation is logged and accessible. No more "I didn\'t get the message" excuses.',
      color: 'bg-blue-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Security & Privacy Risks',
      stat: 'Schools using WhatsApp face 3x higher data breach risks',
      description: 'Sensitive student data, mark sheets, personal information, and financial details are being shared on WhatsApp groups. Anyone in the group can screenshot, download, and share this information. Schools have no control over data once it\'s on WhatsApp. It\'s a ticking time bomb for privacy violations.',
      solution: 'Truyon is built with education-grade security. Data is encrypted, access is controlled, and information is only visible to authorized people. No more data leaks.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'No Analytics or Insights',
      stat: 'Schools cannot track parent engagement or communication effectiveness',
      description: 'How many parents actually read the circular? Which messages get the most engagement? What topics generate the most concern? Schools using WhatsApp operate in the dark. They have no data on communication effectiveness, no insights on parent concerns, and no way to improve their communication strategy.',
      solution: 'Truyon provides powerful analytics. See which parents have read announcements, track engagement trends, and make data-driven decisions about your communication strategy.',
      color: 'bg-emerald-50'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Walk into any school, college, or university in India today, and you'll see the same scene. Teachers are glued to their phones, scrolling through WhatsApp groups. Parents are frustrated because they missed an important announcement. Administrators are exhausted from replying to hundreds of messages.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">WhatsApp has become the default communication tool in education.</strong> But here's the uncomfortable truth—it was never designed for it. What started as a convenient way to share quick updates has become a chaotic, unmanageable mess that's hurting teachers, confusing parents, and putting schools at risk.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Let's be honest about what's really happening with WhatsApp in our schools, and why we need something better.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">10+</div>
          <div className="text-sm text-slate-500">WhatsApp groups per teacher</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">60%</div>
          <div className="text-sm text-slate-500">Messages go unread</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">2+ hrs</div>
          <div className="text-sm text-slate-500">Daily on WhatsApp responses</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why WhatsApp is Failing Our Schools</h2>

      {/* Problems List */}
      {problems.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h3 className="text-xl font-bold text-slate-900">{item.title}</h3>
                <span className="bg-white/80 px-3 py-1 rounded-full text-xs font-bold text-amber-600 border border-amber-200">
                  {item.stat}
                </span>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4 text-sm">{item.description}</p>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Solves This</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">WhatsApp vs. Truyon: The Real Difference</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Using WhatsApp</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Messages get buried and missed</span></li>
              <li className="flex items-start gap-2">• <span>No official record of communication</span></li>
              <li className="flex items-start gap-2">• <span>Teachers work 24/7 responding to messages</span></li>
              <li className="flex items-start gap-2">• <span>Data privacy and security risks</span></li>
              <li className="flex items-start gap-2">• <span>No way to track engagement</span></li>
              <li className="flex items-start gap-2">• <span>Messages can be deleted or lost</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">With Truyon ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Structured communication by department/class</span></li>
              <li className="flex items-start gap-2">• <span>Complete audit trail of all communication</span></li>
              <li className="flex items-start gap-2">• <span>Communication within defined hours</span></li>
              <li className="flex items-start gap-2">• <span>Education-grade security and data protection</span></li>
              <li className="flex items-start gap-2">• <span>Analytics and insights on engagement</span></li>
              <li className="flex items-start gap-2">• <span>Permanent, verifiable communication records</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Real Story Section */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Human Cost</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Behind the numbers and statistics are real people. Teachers who are exhausted. Parents who are confused. Administrators who are overwhelmed. 
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          WhatsApp was meant to make communication easier. Instead, it's made everything harder. Our teachers are burning out because they're expected to be available 24/7. Our parents are frustrated because they can't find important information. Our schools are at risk because there are no official records.
        </p>
        <p className="text-amber-800 leading-relaxed">
          It's time for something better. Something designed for education, not for casual chatting.
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">The Bottom Line</h3>
        <p className="text-indigo-800 leading-relaxed">
          WhatsApp is a great app for staying in touch with friends and family. But it's not a communication system for education. Schools need structure, records, security, and insights—things that WhatsApp simply cannot provide.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon gives schools everything they need to communicate effectively, reduce teacher burnout, keep parents informed, and maintain proper records. It's not just better than WhatsApp. It's a completely different approach to school communication.
        </p>
      </div>
    </>
  );
};

export default ERPvsWhatsApp;